import { create } from 'zustand';

/**
 * Lightweight directory entry for client-side avatar/name resolution.
 * Hydrated once per session from `GET /users/directory` and read by
 * `<UserAvatar />` (and any other UI that references a user by id).
 *
 * Why this exists: Phase S.2 stripped avatar bytes from list payloads
 * (Jobs Queue, etc.) on the assumption that the frontend would resolve
 * avatars from a cache. This store is that cache. See
 * `wiki/entities/user-avatars.md`.
 */
export interface DirectoryUser {
  id: number;
  name: string;
  email: string;
  role: string;
  permit_role: string | null;
  avatar_url: string | null;
}

interface UsersState {
  byId: Map<number, DirectoryUser>;
  loaded: boolean;
  loading: boolean;
  /** ms timestamp of last successful hydrate; 0 means never. Used by `refresh()` to throttle. */
  lastHydratedAt: number;
  error: string | null;

  /** Fetch the directory unconditionally (still skipped if a fetch is in flight). */
  hydrate: (token: string) => Promise<void>;
  /**
   * Soft refresh: hydrate only if at least `minIntervalMs` ms have passed since
   * the last successful fetch. Used by AuthProvider to wire focus/interval/visibility
   * triggers without causing thundering herds when several events fire close together.
   */
  refresh: (token: string, minIntervalMs?: number) => Promise<void>;
  upsert: (user: DirectoryUser) => void;
  clear: () => void;
  getUser: (id: number | null | undefined) => DirectoryUser | undefined;
}

export const useUsers = create<UsersState>()((set, get) => ({
  byId: new Map(),
  loaded: false,
  loading: false,
  lastHydratedAt: 0,
  error: null,

  hydrate: async (token: string) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/users/directory`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Firebase-Auth': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`directory fetch ${res.status}`);
      const data = await res.json();
      const next = new Map<number, DirectoryUser>();
      for (const u of data.users ?? []) {
        next.set(u.id, {
          id: u.id,
          name: u.name,
          email: u.email ?? '',
          role: u.role,
          permit_role: u.permit_role ?? null,
          avatar_url: u.avatar_url ?? null,
        });
      }
      set({ byId: next, loaded: true, loading: false, lastHydratedAt: Date.now() });
    } catch (e: any) {
      set({ loading: false, error: e?.message ?? String(e) });
    }
  },

  refresh: async (token: string, minIntervalMs = 30_000) => {
    const { lastHydratedAt, loading } = get();
    if (loading) return;
    if (Date.now() - lastHydratedAt < minIntervalMs) return;
    await get().hydrate(token);
  },

  upsert: (user: DirectoryUser) => {
    const next = new Map(get().byId);
    next.set(user.id, user);
    set({ byId: next });
  },

  clear: () => set({ byId: new Map(), loaded: false, loading: false, lastHydratedAt: 0, error: null }),

  getUser: (id) => {
    if (id === null || id === undefined) return undefined;
    return get().byId.get(id);
  },
}));
